import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Result.css';
import { BRAND_LOGO, BRAND_NAME, BRAND_NAME_UPPER, BRAND_YEAR, getPrizeImageUrl, normalizeAssetUrl } from '../brand';

const Result = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchResult();
  }, [token]);

  const fetchResult = async () => {
    try {
      const response = await api.get(`/game/result/${token}`);
      setResult(response.data);
    } catch (err) {
      console.error('Erreur récupération résultat:', err);
      setError('Erreur lors de la récupération du résultat');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="result-container">
        <div className="result-loading">
          <div className="spinner-border text-danger" role="status"></div>
          <p>Chargement du résultat...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="result-container">
        <div className="result-error">
          <div className="error-icon">❌</div>
          <h2>Erreur</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const isWinner = result?.is_winner && result?.prize_label;
  const isTryAgain = !isWinner && result?.played;
  const prizeThumbUrl = isWinner
    ? (normalizeAssetUrl(result?.icon) || getPrizeImageUrl(result?.prize_type, result?.prize_label, result?.prize_name))
    : null;

  return (
    <div className="result-container">
      {/* Stars decoration */}
      <div style={{ position: 'absolute', top: '20px', left: '30px', fontSize: '35px', color: 'var(--brand-primary)', opacity: '0.8' }}>★</div>
      <div style={{ position: 'absolute', top: '50px', left: '70px', fontSize: '22px', color: 'var(--text)', opacity: '0.6' }}>★</div>
      <div style={{ position: 'absolute', top: '25px', right: '40px', fontSize: '30px', color: 'var(--brand-primary)', opacity: '0.7' }}>★</div>
      <div style={{ position: 'absolute', top: '65px', right: '85px', fontSize: '18px', color: 'var(--text)', opacity: '0.5' }}>★</div>

      {/* Header */}
      <header className="result-header">
        <div className="brand-logo">
          <span style={{ fontSize: '35px', color: 'var(--brand-primary)' }}>★</span>
          <img src={BRAND_LOGO} alt={BRAND_NAME} style={{ width: '130px', height: 'auto' }} />
          <h1>{BRAND_NAME_UPPER}</h1>
          <span style={{ fontSize: '35px', color: 'var(--brand-deep)' }}>★</span>
        </div>
      </header>

      {/* Main content */}
      <main className="result-main">
        {!result?.played ? (
          <div className="result-not-played">
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>🎫</div>
            <h2>Carte à gratter</h2>
            <p>Vous n'avez pas encore gratté votre carte.</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/play/${token}`)}
              style={{
                background: 'linear-gradient(180deg, var(--brand-primary) 0%, var(--brand-primary-darker) 50%, var(--brand-deep) 100%)',
                border: '3px solid var(--brand-deep)',
                borderRadius: '15px',
                padding: '15px 35px',
                marginTop: '25px',
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                boxShadow: '0 8px 25px rgba(30, 43, 36, 0.12)'
              }}
            >
              ★ GRATTER MA CARTE ★
            </button>
          </div>
        ) : isWinner ? (
          <div className="result-winner">
            <div className="confetti"></div>
            <div className="confetti"></div>
            <div className="confetti"></div>

            <div style={{ fontSize: '80px', marginBottom: '20px', animation: 'bounce 0.6s ease-out' }}>🎉</div>
            <h2 style={{
              color: 'var(--text)',
              fontSize: '36px',
              marginBottom: '10px',
              textTransform: 'uppercase',
              letterSpacing: '2px'
            }}>
              ★ FÉLICITATIONS ! ★
            </h2>
            <p className="winner-subtitle">Vous avez gagné :</p>

            <div className="prize-display">
              {prizeThumbUrl ? (
                <img className="result-prize-image" src={prizeThumbUrl} alt={result.prize_label} loading="lazy" />
              ) : (
                <div style={{ fontSize: '64px', marginBottom: '15px' }}>🎁</div>
              )}
              <h3 className="prize-name">{result.prize_label}</h3>
              {result.prize_code && (
                <div className="prize-code-box">
                  <p className="code-label">Votre code de gain :</p>
                  <p className="code-value">{result.prize_code}</p>
                </div>
              )}
            </div>

            <div className="winner-instructions">
              <p>📸 Prenez une capture de ce code</p>
              <p>🏪 Présentez-la à une hôtesse pour récupérer votre lot</p>
            </div>

            {result.plays_history && result.plays_history.length > 0 && (
              <div className="history-section">
                <h4>★ Historique de vos parties ★</h4>
                {result.plays_history.map((play, index) => (
                  <div key={index} className="history-item">
                    <span>Partie {play.play_number}</span>
                    <span className={play.prize_label ? 'won' : 'lost'}>
                      {play.prize_label || 'Réessayez'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : isTryAgain ? (
          <div className="result-try-again">
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>😢</div>
            <h2 style={{ color: 'var(--text)', fontSize: '28px' }}>Dommage !</h2>
            <p>Vous n'avez pas gagné cette fois-ci.</p>
            <p style={{ fontSize: '16px', color: 'var(--brand-deep)', fontWeight: 'bold', marginTop: '20px' }}>
              ★ Retentez votre chance lors de votre prochain achat {BRAND_NAME} ! ★
            </p>

            {result.plays_history && result.plays_history.length > 0 && (
              <div className="history-section">
                <h4>★ Historique de vos parties ★</h4>
                {result.plays_history.map((play, index) => (
                  <div key={index} className="history-item">
                    <span>Partie {play.play_number}</span>
                    <span className={play.prize_label ? 'won' : 'lost'}>
                      {play.prize_label || 'Réessayez'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="result-unknown">
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>❓</div>
            <h2>Résultat non disponible</h2>
            <p>Veuillez contacter une hôtesse si vous pensez qu'il s'agit d'une erreur.</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="result-footer">
        <span style={{ fontSize: '18px', color: 'var(--brand-primary)' }}>★</span>
        <p>© {BRAND_YEAR} {BRAND_NAME_UPPER}.</p>
        <span style={{ fontSize: '18px', color: 'var(--brand-deep)' }}>★</span>
      </footer>
    </div>
  );
};

export default Result;
